import React from 'react';
import './WorkItemCard.css';

const WorkItemCard = ({ workItem, onDragStart, onEdit, onView, onDelete, canDelete }) => {
  // Helper function to abbreviate names
  const abbreviateName = (fullName) => {
    if (!fullName) return '';
    const names = fullName.trim().split(' ');
    if (names.length === 1) return names[0];
    return `${names[0]} ${names[names.length - 1].charAt(0)}.`;
  };
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  const calculateItemsCount = () => {
    const servicosCount = workItem.selectedServicos?.length || 0;
    const activitiesCount = workItem.customActivities?.length || 0;
    const reembolsosCount = workItem.reembolsos?.length || 0;
    return servicosCount + activitiesCount + reembolsosCount;
  };

  const calculateCompletedCount = () => {
    const servicosCompleted = workItem.selectedServicos?.filter(s => s.completed).length || 0;
    const activitiesCompleted = workItem.customActivities?.filter(a => a.completed).length || 0;
    const reembolsosCompleted = workItem.reembolsos?.filter(r => r.completed).length || 0;
    return servicosCompleted + activitiesCompleted + reembolsosCompleted;
  };

  const totalItems = calculateItemsCount();
  const completedItems = calculateCompletedCount();
  const progressPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div
      className={`work-item-card ${!workItem.authorized ? 'unauthorized' : ''} ${workItem.editLocked ? 'edit-locked' : ''} ${workItem.paymentLocked ? 'payment-locked' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, workItem)}
      onClick={() => onView(workItem)}
    >
      <div className="card-header">
        <h4 className="card-title">
          {!workItem.authorized && (
            <span className="lock-icon" title="Aguardando autorização do cliente">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="11" width="14" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </span>
          )}
          {workItem.editLocked && (
            <span className="edit-lock-icon" title="Bloqueado para edição - requer aprovação do cliente">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="11" width="14" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </span>
          )}
          {workItem.paymentLocked && (
            <span className="payment-lock-icon" title="Bloqueado - pagamento recebido">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                <path d="M11 15v2"/>
                <path d="M11 8v3"/>
              </svg>
            </span>
          )}
          {workItem.title}
        </h4>
        <div className="card-actions">
          <button
            className="card-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(workItem);
            }}
            title="Editar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          {canDelete && (
            <button
              className="card-action-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(workItem._id);
              }}
              title="Excluir"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="card-content">
        {workItem.clienteData && (
          <div className="card-cliente">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span>{workItem.clienteData.nome}</span>
          </div>
        )}

        {!workItem.authorized && (
          <div className="authorization-status">
            <span className="auth-badge pending">
              🔒 Aguardando Autorização
            </span>
          </div>
        )}
        
        {workItem.activeEditRequest && (
          <div className="pending-badges">
            <span className="pending-edit-badge" title="Solicitação de edição pendente">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Edição Pendente
            </span>
          </div>
        )}
        
        {workItem.pendingHighValueApprovals?.length > 0 && (
          <div className="pending-badges">
            <span className="high-value-badge" title="Aprovação de alto valor pendente">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              Alto Valor
            </span>
          </div>
        )}
        
        {workItem.comments?.length > 0 && (
          <div className="comment-indicator" title={`${workItem.comments.length} comentário${workItem.comments.length > 1 ? 's' : ''}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
            <span className="comment-badge">{workItem.comments.length}</span>
          </div>
        )}



        <div className="card-info">
          <div className="card-priority">
            <span 
              className="priority-badge"
              style={{ backgroundColor: getPriorityColor(workItem.priority) }}
            >
              {getPriorityLabel(workItem.priority)}
            </span>
          </div>

          {workItem.dueDate && (
            <div className="card-due-date">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>{formatDate(workItem.dueDate)}</span>
            </div>
          )}
        </div>

        <div className="card-footer">
          <div className="card-footer-top">
            <div className="card-items-info">
              <div className="card-items-count">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
                <span>{completedItems}/{totalItems} itens</span>
              </div>
              {totalItems > 0 && (
                <div className="card-progress-mini">
                  <div 
                    className="progress-mini-fill" 
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              )}
            </div>
            
            <div className="card-values">
              <div className="card-total" title="Total Consolidado">
                <strong>{formatCurrency(workItem.totalConsolidado || 0)}</strong>
              </div>
              <div className="card-total-previsto" title="Total Previsto">
                {formatCurrency(workItem.totalValue)}
              </div>
            </div>
          </div>
          
          <div className="card-footer-users">
            <div className="card-user-info" title={`Criado por: ${workItem.createdByData?.nomeCompleto || '-'}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span>{abbreviateName(workItem.createdByData?.nomeCompleto)}</span>
            </div>
            <div className="card-user-info" title={`Responsável: ${workItem.assignedToData?.nomeCompleto || 'Não atribuído'}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <path d="M21 10h-4m2-2v4" />
              </svg>
              <span className={!workItem.assignedToData ? 'unassigned' : ''}>
                {workItem.assignedToData ? abbreviateName(workItem.assignedToData.nomeCompleto) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkItemCard;
